<script setup>
import { useForm, Link } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import BookingSubMenu from '@/Includes/OtherBookingSubMenu.vue';
import { ref, onMounted, computed } from "vue";
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useToast } from 'vue-toast-notification';
import Loading from 'vue-loading-overlay';
import { Modal } from 'bootstrap';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

const props = defineProps({
    year: String,
    bookingById: Object,
    flash: Object,
    agent: Object,
    user: Object,
});

let isLoading = ref(false);
let modalShow = ref(null);
let thisModalObj = null;
const $toast = useToast();
let menu = 'details';
dayjs.extend(advancedFormat);

// Calculate duration of stay
const calculateDuration = (arrival, departure) => {
    const start = dayjs(arrival);
    const end = dayjs(departure);
    return end.diff(start, 'day');
};

// Format date to be more readable
const formatDate = (date) => {
    return dayjs(date).format('Do MMMM YYYY');
};

// Get status badge class
const getStatusBadgeClass = computed(() => {
    const statusMap = {
        'Pending': 'bg-label-dark',
        'SendConfirmation': 'bg-label-warning',
        'Confirmed': 'bg-label-info',
        'Cancelled': 'bg-label-danger',
        'Paid': 'bg-label-success'
    };
    return statusMap[props.bookingById.status] || 'bg-label-secondary';
});

// Get status text
const getStatusText = computed(() => {
    if (props.bookingById.status === 'SendConfirmation') {
        return 'Awaiting Confirmation';
    }
    return props.bookingById.status;
});

function formatPrice(value) {
    let val = (value / 1).toFixed(2).replace('.', '.');
    return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

onMounted(() => {
    // Only initialize modal if element exists
    if (modalShow.value) {
        thisModalObj = new Modal(modalShow.value);
    }

    if (props.flash.message) {
        $toast.open({
            message: props.flash.message,
            type: "info",
            duration: 4000,
            position: 'top-right',
            class: 'bs-toast toast bg-dark show'
        });
    }
});

const FlightDetailsForm = useForm({
    arrival_flight: '',
    departure_flight: '',
    booking_id: props.bookingById.id,
});

const UpdateResortAmountForm = useForm({
    resort_amount: '',
    booking_id: props.bookingById.id,
});

const BookingCancelForm = useForm({
    cancelled_reason: '',
    booking_id: props.bookingById.id,
});

const BookingConfirmForm = useForm({
    resort_confirmation_number: '',
    sendmailtoagent: false,
    booking_id: props.bookingById.id,
});

const configArrv = ref({
    dateFormat: 'Y-m-d',
    allowInput: true,
    static: true,
    // These options help with styling
    wrap: false,
    disableMobile: true,
    // Theme can be 'light', 'dark', 'material_blue', etc.
    theme: 'light'
});

const proformaForm = useForm({
    booking_id: props.bookingById.id,
    booking_no: props.bookingById.booking_no,
    currency: 'USD',
    payment_due: dayjs().add(7, 'day').format('YYYY-MM-DD'),
    remarks: '',
    amount: '',
    invdetails: []
});

const taxInvoiceForm = useForm({
    booking_id: props.bookingById.id,
    booking_no: props.bookingById.booking_no,
    currency: 'INR',  // Tax invoice is usually in INR
    room_rate: '',
    total_payable: '',
});

const UpdateResortAmountFormSubmit = () => {
    UpdateResortAmountForm.post('/bookings/updateresortamount', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            thisModalObj.hide();
            $toast.open({
                message: props.flash.message,
                type: "info",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
        }
    });
};

const BookingCancelFormSubmit = () => {
    BookingCancelForm.post('/other-bookings/cancel', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            // Close the modal
            const modal = Modal.getInstance(document.getElementById('cancelBookingModal'));
            if (modal) modal.hide();

            // Update booking status in the component
            props.bookingById.status = 'Cancelled';
            props.bookingById.cancelled_reason = BookingCancelForm.cancelled_reason;

            // Show success message
            $toast.open({
                message: 'Booking has been cancelled successfully.',
                type: "info",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
        }
    });
};

const BookingConfirmFormSubmit = () => {
    BookingConfirmForm.post('/other-bookings/confirmOtherbooking', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            // Get the specific modal instance and hide it
            const modal = Modal.getInstance(document.getElementById('bookingComfirmModal'));
            if (modal) modal.hide();

            $toast.open({
                message: props.flash.message,
                type: "info",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
        }
    });
};

function FormReset() {
    FlightDetailsForm.reset();
    FlightDetailsForm.clearErrors();
    BookingCancelForm.reset();
    BookingCancelForm.clearErrors();
    BookingConfirmForm.reset();
    BookingConfirmForm.clearErrors();
    thisModalObj.hide();
}

function openModal(modalId) {
    const modal = new Modal(document.getElementById(modalId));
    modal.show();
}

function resetProformaForm() {
    proformaForm.reset();
    proformaForm.booking_id = props.bookingById.id;
    proformaForm.booking_no = props.bookingById.booking_no;
    proformaForm.currency = 'USD';
    proformaForm.payment_due = dayjs().add(7, 'day').format('YYYY-MM-DD');

    // Close the modal
    const modal = Modal.getInstance(document.getElementById('createProformaModal'));
    if (modal) modal.hide();
}

function createProformaInvoice() {
    // Create a single invoice detail with combined information
    const invdetails = [];

    // Calculate total nights
    const totalNights = getTotalNights();

    // Create a combined description of all hotels
    let description = '';

    if (props.bookingById.hotel_included === 'yes' && props.bookingById.otherbookinglocations && props.bookingById.otherbookinglocations.length > 0) {
        // Combine all hotel descriptions
        description = props.bookingById.otherbookinglocations.map(location =>
            `${location.hotel_name} for ${location.nights} nights`
        ).join(', ');
    } else if (props.bookingById.sight_seeing) {
        // If it's a sightseeing tour without hotels, use remarks or a default description
        description = proformaForm.remarks || 'Sightseeing Tour';
    } else {
        // If no hotels and no sightseeing, use a generic description
        description = proformaForm.remarks || `Accommodation for ${totalNights} nights`;
    }

    // Add a single invoice detail with the total amount
    invdetails.push({
        qty: totalNights || 1, // Use 1 if no nights calculated (sightseeing only)
        description: description,
        rate: parseFloat(proformaForm.amount) / (totalNights || 1), // Use 1 if no nights calculated
        amount: parseFloat(proformaForm.amount)
    });

    proformaForm.invdetails = invdetails;

    // Submit the form
    proformaForm.post('/otherinvoices/store', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            resetProformaForm();
            $toast.open({
                message: 'Proforma invoice created successfully!',
                type: "success",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
        }
    });
}

function getTotalNights() {
    if (props.bookingById.hotel_included === 'yes' && props.bookingById.otherbookinglocations) {
        return props.bookingById.otherbookinglocations.reduce((total, location) => {
            return total + parseInt(location.nights || 0);
        }, 0);
    }
    return calculateDuration(props.bookingById.arrival_date, props.bookingById.departure_date);
}

// Add this function to calculate the total amount for an invoice
function calculateInvoiceTotal(invoice) {
    let total = 0;
    if (invoice.invoicedetails && invoice.invoicedetails.length > 0) {
        invoice.invoicedetails.forEach(detail => {
            total += parseFloat(detail.amount || 0);
        });
    }
    return formatPrice(total);
}

function resetTaxInvoiceForm() {
    taxInvoiceForm.reset();
    taxInvoiceForm.booking_id = props.bookingById.id;
    taxInvoiceForm.booking_no = props.bookingById.booking_no;
    taxInvoiceForm.currency = 'INR';

    // Close the modal
    const modal = Modal.getInstance(document.getElementById('createTaxInvoiceModal'));
    if (modal) modal.hide();
}

function createTaxInvoice() {
    taxInvoiceForm.post('/otherinvoices/storeOtherTax', {
        preserveScroll: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
        onSuccess: () => {
            resetTaxInvoiceForm();
            $toast.open({
                message: 'Tax invoice created successfully!',
                type: "success",
                duration: 4000,
                position: 'top-right',
                class: 'bs-toast toast bg-dark show'
            });
        }
    });
}
</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import "vue-loading-overlay/dist/vue-loading.css";

.booking-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
}

.booking-status {
    display: inline-flex;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
}

.booking-number {
    font-size: 1.25rem;
    font-weight: 600;
}

.booking-actions {
    display: flex;
    gap: 0.5rem;
}

.info-card {
    height: 100%;
    transition: all 0.3s ease;
}

.info-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 0.25rem 1rem rgba(161, 172, 184, 0.45);
}

.info-card-icon {
    font-size: 2rem;
    margin-bottom: 1rem;
}

.hotel-card {
    border-left: 4px solid #696cff;
    margin-bottom: 1rem;
}

.timeline-item {
    position: relative;
    padding-left: 3rem;
    padding-bottom: 1.5rem;
}

.timeline-item::before {
    content: "";
    position: absolute;
    left: 0.85rem;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #d9dee3;
}

.timeline-point {
    position: absolute;
    left: 0;
    top: 0;
    width: 1.75rem;
    height: 1.75rem;
    border-radius: 50%;
    background-color: #fff;
    border: 2px solid #696cff;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;
}

.timeline-content {
    padding: 1rem;
    background-color: #f5f5f9;
    border-radius: 0.375rem;
}

.confirmation-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.25rem 0.75rem;
    border-radius: 0.375rem;
    font-size: 0.875rem;
}
</style>
<template>
    <AppLayout title="Booking Details" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">

            <!-- Booking Header with Status and Actions -->
            <div class="card mb-4">
                <div class="card-body booking-header">
                    <div>
                        <div class="d-flex align-items-center mb-2">
                            <div :class="['booking-status', getStatusBadgeClass]">
                                <i class="bx bx-check-circle me-2"></i>
                                <span>{{ getStatusText }}</span>
                            </div>
                            <span class="ms-3" v-if="bookingById.resort_confirmation_number">
                                <div class="confirmation-badge bg-label-success">
                                    <i class="bx bx-check-shield"></i>
                                    <span>Confirmation: {{ bookingById.resort_confirmation_number }}</span>
                                </div>
                            </span>
                        </div>
                        <div class="booking-number">
                            Booking #{{ bookingById.booking_no }}
                            <span class="text-muted ms-2" style="font-size: 0.875rem;">
                                ({{ calculateDuration(bookingById.arrival_date, bookingById.departure_date) }} days)
                            </span>
                        </div>
                    </div>

                    <div class="booking-actions">
                        <Link :href="'/other-bookings/edit/' + bookingById.id"
                            v-if="$page.props.user.permissions.includes('edit-bookings')" class="btn btn-primary">
                        <i class="bx bx-edit me-1"></i> Edit Booking
                        </Link>
                        <button v-if="bookingById.status !== 'Confirmed' && bookingById.status !== 'Cancelled'"
                            class="btn btn-success" @click="openModal('bookingComfirmModal')">
                            <i class="bx bx-check-circle me-1"></i> Confirm Booking
                        </button>
                        <button v-if="bookingById.status !== 'Cancelled'" class="btn btn-danger"
                            @click="openModal('cancelBookingModal')">
                            <i class="bx bx-x-circle me-1"></i> Cancel Booking
                        </button>
                        <button class="btn btn-info" @click="openModal('createProformaModal')"
                            v-if="$page.props.user.permissions.includes('add-proforma-invoice') && bookingById.status == 'Confirmed'">
                            <i class="bx bx-file me-1"></i> Create Proforma
                        </button>
                        <button class="btn btn-warning" @click="openModal('createTaxInvoiceModal')"
                            v-if="$page.props.user.permissions.includes('add-tax-invoice') && bookingById.status == 'Confirmed'">
                            <i class="bx bx-receipt me-1"></i> Create Tax Invoice
                        </button>
                    </div>
                </div>
            </div>

            <!-- Cancellation Alert -->
            <div v-if="bookingById.status === 'Cancelled'" class="alert alert-danger mb-4" role="alert">
                <div class="d-flex">
                    <i class="bx bx-error-circle fs-3 me-2"></i>
                    <div>
                        <h6 class="alert-heading mb-1">Booking Cancelled</h6>
                        <span>{{ bookingById.cancelled_reason }}</span>
                    </div>
                </div>
            </div>

            <!-- Key Information Cards -->
            <div class="row mb-4">
                <div class="col-md-3">
                    <div class="card info-card">
                        <div class="card-body text-center">
                            <i class="bx bx-calendar info-card-icon text-primary"></i>
                            <h5 class="card-title">Check-in</h5>
                            <p class="card-text">{{ formatDate(bookingById.arrival_date) }}</p>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card info-card">
                        <div class="card-body text-center">
                            <i class="bx bx-calendar-x info-card-icon text-danger"></i>
                            <h5 class="card-title">Check-out</h5>
                            <p class="card-text">{{ formatDate(bookingById.departure_date) }}</p>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card info-card">
                        <div class="card-body text-center">
                            <i class="bx bx-building-house info-card-icon text-success"></i>
                            <h5 class="card-title">Stay Type</h5>
                            <p class="card-text">{{ bookingById.stay_type == 0 ? 'Single Stay' : 'Split Stay' }}</p>
                        </div>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card info-card">
                        <div class="card-body text-center">
                            <i class="bx bx-restaurant info-card-icon text-warning"></i>
                            <h5 class="card-title">Meal Type</h5>
                            <p class="card-text">{{ bookingById.meal_type || 'Not Specified' }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Add this section to display guest information -->
            <div class="card mb-4">
                <div class="card-header">
                    <h5 class="mb-0">Guest Information</h5>
                </div>
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-6">
                            <h6 class="text-muted mb-2">Guest Name</h6>
                            <p class="d-flex align-items-center">
                                <i class="bx bx-user me-2 text-primary"></i>
                                {{ bookingById.guest_name || 'Not specified' }}
                            </p>
                        </div>
                        <div class="col-md-3">
                            <h6 class="text-muted mb-2">Adults</h6>
                            <p class="d-flex align-items-center">
                                <i class="bx bx-group me-2 text-primary"></i>
                                {{ bookingById.number_of_adults || 1 }}
                            </p>
                        </div>
                        <div class="col-md-3">
                            <h6 class="text-muted mb-2">Children</h6>
                            <p class="d-flex align-items-center">
                                <i class="bx bx-child me-2 text-primary"></i>
                                {{ bookingById.number_of_children || 0 }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Detailed Information -->
            <div class="row">
                <!-- Left Column - Hotel Information -->
                <div class="col-md-8">
                    <div class="card mb-4">
                        <div class="card-header d-flex justify-content-between align-items-center">
                            <h5 class="mb-0">Hotel & Stay Information</h5>
                        </div>
                        <div class="card-body">
                            <!-- Hotel Information -->
                            <div v-if="bookingById.hotel_included === 'yes'">
                                <h6 class="text-primary mb-3">Hotel Details</h6>
                                <div v-for="(location, index) in bookingById.otherbookinglocations" :key="location.id"
                                    class="card hotel-card mb-3">
                                    <div class="card-body">
                                        <div class="d-flex justify-content-between align-items-center mb-2">
                                            <h6 class="mb-0">
                                                <i class="bx bx-hotel me-1"></i>
                                                Hotel {{ index + 1 }}: {{ location.hotel_name || location.location_name
                                                }}
                                            </h6>
                                            <span class="badge bg-label-primary">{{ location.nights }} Nights</span>
                                        </div>
                                        <div class="row">
                                            <div class="col-md-6">
                                                <small class="text-muted">Check-in</small>
                                                <p>{{ location.check_in ? formatDate(location.check_in)
                                                    : 'Not specified' }}</p>
                                            </div>
                                            <div class="col-md-6">
                                                <small class="text-muted">Check-out</small>
                                                <p>{{ location.check_out ? formatDate(location.check_out)
                                                    : 'Not specified' }}</p>
                                            </div>
                                            <div class="col-md-12" v-if="location.meal_type">
                                                <small class="text-muted">Meal Plan</small>
                                                <p>{{ location.meal_type }}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Sight Seeing Information -->
                            <div v-if="bookingById.sight_seeing" class="mt-4">
                                <h6 class="text-primary mb-3">Sight Seeing</h6>
                                <div class="card">
                                    <div class="card-body">
                                        <p v-html="bookingById.sight_seeing"></p>
                                    </div>
                                </div>
                            </div>

                            <!-- Car Information -->
                            <div v-if="bookingById.car_used" class="mt-4">
                                <h6 class="text-primary mb-3">Car Details</h6>
                                <div class="card">
                                    <div class="card-body">
                                        <p><i class="bx bx-car me-2"></i>{{ bookingById.car_used }}</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Itinerary Timeline -->
                            <div v-if="bookingById.itineraries && bookingById.itineraries.length > 0" class="mt-4">
                                <h6 class="text-primary mb-3">Itinerary</h6>
                                <div class="timeline-2">
                                    <div v-for="(item, index) in bookingById.itineraries" :key="index"
                                        class="timeline-item">
                                        <div class="timeline-point">
                                            <i class="bx bx-calendar-event"></i>
                                        </div>
                                        <div class="timeline-content">
                                            <h6 class="mb-1">Day {{ item.day }}</h6>
                                            <p class="mb-0">{{ item.description }}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right Column - Travel & Agent Information -->
                <div class="col-md-4">
                    <!-- Travel Information -->
                    <div class="card mb-4">
                        <div class="card-header">
                            <h5 class="mb-0">Travel Information</h5>
                        </div>
                        <div class="card-body">
                            <div class="mb-3">
                                <h6 class="text-muted mb-2">Arrival Flight / Info</h6>
                                <p class="d-flex align-items-center">
                                    <i class="bx bx-plane-take-off me-2 text-primary"></i>
                                    {{ bookingById.arrival_flight || 'Not specified' }}
                                </p>
                            </div>
                            <div class="mb-3">
                                <h6 class="text-muted mb-2">Departure Flight / Info</h6>
                                <p class="d-flex align-items-center">
                                    <i class="bx bx-plane-land me-2 text-danger"></i>
                                    {{ bookingById.departure_flight || 'Not specified' }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Agent Information -->
                    <div class="card mb-4">
                        <div class="card-header">
                            <h5 class="mb-0">Agent Information</h5>
                        </div>
                        <div class="card-body">
                            <div class="d-flex align-items-center mb-3">
                                <div class="avatar avatar-md me-3 bg-label-primary">
                                    <span class="avatar-initial rounded-circle">
                                        {{ bookingById.agent.agency_name.charAt(0) }}
                                    </span>
                                </div>
                                <div>
                                    <h6 class="mb-0">{{ bookingById.agent.agency_name }}</h6>
                                    <small class="text-muted">{{ bookingById.agent.agency_email || 'No email provided'
                                    }}</small>
                                </div>
                            </div>
                            <div class="mb-3" v-if="bookingById.agent.phone">
                                <h6 class="text-muted mb-2">Contact</h6>
                                <p class="d-flex align-items-center">
                                    <i class="bx bx-phone me-2"></i>
                                    {{ bookingById.agent.phone }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Payment Information -->
                    <div class="card mb-4" v-if="bookingById.payment_deadline || bookingById.payment_details">
                        <div class="card-header">
                            <h5 class="mb-0">Payment Information</h5>
                        </div>
                        <div class="card-body">
                            <div class="mb-3" v-if="bookingById.payment_deadline">
                                <h6 class="text-muted mb-2">Payment Deadline</h6>
                                <p class="d-flex align-items-center">
                                    <i class="bx bx-calendar-exclamation me-2 text-warning"></i>
                                    {{ formatDate(bookingById.payment_deadline) }}
                                </p>
                            </div>
                            <div v-if="bookingById.payment_details">
                                <h6 class="text-muted mb-2">Payment Details</h6>
                                <p>{{ bookingById.payment_details }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Remarks Section -->
            <div class="card mb-4">
                <div class="card-header">
                    <h5 class="mb-0">Remarks</h5>
                </div>
                <div class="card-body">
                    <div class="alert alert-dark d-flex mb-0" role="alert">
                        <span class="badge badge-center rounded-pill bg-success p-3 me-2">
                            <i class="bx bx-info-circle fs-5"></i>
                        </span>
                        <div class="d-flex flex-column ps-1">
                            <div v-html="bookingById.remarks"></div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- First card for Proforma Invoices -->
            <div class="card mb-4" v-if="bookingById.otherinvoices && bookingById.otherinvoices.length > 0">
                <div class="card-header">
                    <h5 class="mb-0">Proforma Invoices</h5>
                </div>
                <div class="card-body">
                    <div class="table-responsive">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Invoice #</th>
                                    <th>Type</th>
                                    <th>Currency</th>
                                    <th>Amount</th>
                                    <th>Due Date</th>
                                    <th>Status</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="invoice in bookingById.otherinvoices" :key="invoice.id">
                                    <td>{{ invoice.invoice_no }}</td>
                                    <td>{{ invoice.type }}</td>
                                    <td>{{ invoice.currency }}</td>
                                    <td>{{ calculateInvoiceTotal(invoice) }}</td>
                                    <td>{{ formatDate(invoice.payment_due) }}</td>
                                    <td>
                                        <span
                                            :class="['badge', invoice.paid_date ? 'bg-label-success' : 'bg-label-warning']">
                                            {{ invoice.paid_date ? 'Paid' : 'Pending' }}
                                        </span>
                                    </td>
                                    <td>
                                        <div class="btn-group">
                                            <a :href="'/otherinvoices/preview/' + invoice.id"
                                                class="btn btn-sm btn-outline-primary">
                                                <i class="bx bx-show-alt me-1"></i> Preview
                                            </a>
                                            <a :href="'/otherinvoices/delete/' + invoice.id"
                                                class="btn btn-sm btn-outline-danger"
                                                v-if="$page.props.user.permissions.includes('remove-proforma-invoice')"
                                                onclick="return confirm('Are you sure you want to delete this invoice?')">
                                                <i class="bx bx-trash me-1"></i> Delete
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Separate card for Tax Invoices -->
            <div class="card mb-4" v-if="bookingById.othertaxinvoices && bookingById.othertaxinvoices.length > 0">
                <div class="card-header">
                    <h5 class="mb-0">Tax Invoices</h5>
                </div>
                <div class="card-body">
                    <div class="table-responsive">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th>Invoice #</th>
                                    <th>Currency</th>
                                    <th>Room Rate</th>
                                    <th>Total Payable</th>
                                    <th>Created Date</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="invoice in bookingById.othertaxinvoices" :key="invoice.id">
                                    <td>{{ invoice.invoice_no }}</td>
                                    <td>{{ invoice.currency }}</td>
                                    <td>{{ formatPrice(invoice.room_rate) }}</td>
                                    <td>{{ formatPrice(invoice.total_payable) }}</td>
                                    <td>{{ formatDate(invoice.created_at) }}</td>
                                    <td>
                                        <div class="btn-group">
                                            <a :href="'/otherinvoices/previewOtherTax/' + invoice.id"
                                                class="btn btn-sm btn-outline-primary">
                                                <i class="bx bx-show-alt me-1"></i> Preview
                                            </a>
                                            <a :href="'/otherinvoices/deleteTax/' + invoice.id"
                                                class="btn btn-sm btn-outline-danger"
                                                v-if="$page.props.user.permissions.includes('remove-tax-invoice')"
                                                onclick="return confirm('Are you sure you want to delete this invoice?')">
                                                <i class="bx bx-trash me-1"></i> Delete
                                            </a>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Booking Metadata -->
            <div class="card mb-4">
                <div class="card-body">
                    <div class="row">
                        <div class="col-md-4">
                            <small class="text-muted">Created By</small>
                            <p>{{ bookingById.user?.email || 'Unknown' }}</p>
                        </div>
                        <div class="col-md-4">
                            <small class="text-muted">Created At</small>
                            <p>{{ formatDate(bookingById.created_at) }}</p>
                        </div>
                        <div class="col-md-4">
                            <small class="text-muted">Last Updated</small>
                            <p>{{ formatDate(bookingById.updated_at) }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Resort Amount Modal -->
        <div class="modal fade" id="updateResortAmountModal" tabindex="-1" aria-modal="true" role="dialog">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Update Resort Invoice Amount</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <hr>
                    <form @submit.prevent="UpdateResortAmountFormSubmit">
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-md-12">
                                    <label for="first_name" class="form-label"><b>Resort Invoice Amount</b></label>
                                    <input type="number" step=".01" v-model="UpdateResortAmountForm.resort_amount"
                                        name="resort_amount" class="form-control" placeholder="Resort invoice amount"
                                        required="required">
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-label-secondary" @click="FormReset">
                                Close
                            </button>
                            <button type="submit" class="btn btn-primary">Save Changes</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Cancel Booking Modal -->
        <div class="modal fade" id="cancelBookingModal" tabindex="-1" aria-modal="true" role="dialog">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Booking Cancellation</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <hr>
                    <form @submit.prevent="BookingCancelFormSubmit">
                        <div class="modal-body">
                            <div class="alert alert-warning" role="alert">
                                <div class="d-flex">
                                    <i class="bx bx-error-circle fs-3 me-2"></i>
                                    <div>
                                        <h6 class="alert-heading mb-1">Warning!</h6>
                                        <span>Cancelling this booking cannot be undone. Please provide a detailed reason
                                            for cancellation.</span>
                                    </div>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-12">
                                    <label for="cancelled_reason" class="form-label"><b>Cancellation Reason</b></label>
                                    <textarea class="form-control" v-model="BookingCancelForm.cancelled_reason"
                                        name="cancelled_reason" placeholder="Type a reason to cancel!" required
                                        rows="4"></textarea>
                                    <div v-if="BookingCancelForm.errors.cancelled_reason" class="text-danger mt-1">
                                        {{ BookingCancelForm.errors.cancelled_reason }}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-label-secondary" @click="FormReset">
                                Close
                            </button>
                            <button type="submit" class="btn btn-danger" :disabled="BookingCancelForm.processing">
                                <i class="bx bx-x-circle me-1"></i> Cancel Booking
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>


        <!-- Confirm Booking Modal -->
        <div class="modal fade" id="bookingComfirmModal" tabindex="-1" aria-modal="true" role="dialog">
            <div class="modal-dialog modal-dialog-top">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Booking Confirmation</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <hr>
                    <form @submit.prevent="BookingConfirmFormSubmit">
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-md-12">
                                    <label for="first_name" class="form-label"><b>Confirmation Number</b></label>
                                    <input type="text" name="resort_confirmation_number"
                                        v-model="BookingConfirmForm.resort_confirmation_number" class="form-control"
                                        placeholder="Resort Ref Number" required="required">
                                </div>
                            </div>
                            <div class="row">
                                <div>
                                    <div class="col-md-12 form-check mt-4">
                                        <input type="checkbox" v-model="BookingConfirmForm.sendmailtoagent"
                                            class="form-check-input" name="sendmailtoagent" checked="checked">
                                        <label class="form-check-label" for="customCheck1">Send confirmation mail to
                                            agent</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-if="BookingConfirmForm.sendmailtoagent" class="px-4">Once you confirm this booking a mail
                            will be dispatched to the agent.</div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-label-secondary" @click="FormReset">
                                Close
                            </button>
                            <button type="submit" class="btn btn-primary">Confirm</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Create Proforma Invoice Modal -->
        <div class="modal fade" id="createProformaModal" tabindex="-1" aria-modal="true" role="dialog">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Create Proforma Invoice</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <hr>
                    <form @submit.prevent="createProformaInvoice">
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-md-6">
                                    <label for="currency" class="form-label"><b>Currency</b></label>
                                    <select class="form-select" v-model="proformaForm.currency" required>
                                        <option value="USD">USD</option>
                                        <option value="INR">INR</option>
                                    </select>
                                </div>
                                <div class="col-md-6">
                                    <label for="payment_due" class="form-label"><b>Payment Deadline</b></label>
                                    <FlatPickr v-model="proformaForm.payment_due" :config="configArrv"
                                        class="form-control" placeholder="Payment Deadline" required />
                                </div>
                                <div class="col-md-12 mt-3">
                                    <label for="amount" class="form-label"><b>Total Invoice Amount</b></label>
                                    <div class="input-group">
                                        <span class="input-group-text">{{ proformaForm.currency }}</span>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model="proformaForm.amount" placeholder="Enter total invoice amount"
                                            required />
                                    </div>
                                    <small class="text-muted">This is the total amount that will appear on the
                                        invoice.</small>
                                </div>
                                <div class="col-md-12 mt-3">
                                    <label for="remarks" class="form-label"><b>Remarks</b></label>
                                    <textarea class="form-control" v-model="proformaForm.remarks"
                                        placeholder="Enter remarks" rows="3"></textarea>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-label-secondary" @click="resetProformaForm">
                                Close
                            </button>
                            <button type="submit" class="btn btn-primary" :disabled="proformaForm.processing">
                                Create Proforma
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>

        <!-- Create Tax Invoice Modal -->
        <div class="modal fade" id="createTaxInvoiceModal" tabindex="-1" aria-modal="true" role="dialog">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Create Tax Invoice</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <hr>
                    <form @submit.prevent="createTaxInvoice">
                        <div class="modal-body">
                            <div class="row">
                                <div class="col-md-12">
                                    <label for="currency" class="form-label"><b>Currency</b></label>
                                    <select class="form-select" v-model="taxInvoiceForm.currency" required>
                                        <option value="INR">INR</option>
                                        <option value="USD">USD</option>
                                    </select>
                                </div>
                                <div class="col-md-12 mt-3">
                                    <label for="room_rate" class="form-label"><b>Room Rate</b></label>
                                    <div class="input-group">
                                        <span class="input-group-text">{{ taxInvoiceForm.currency }}</span>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model="taxInvoiceForm.room_rate" placeholder="Enter room rate" required />
                                    </div>
                                </div>
                                <div class="col-md-12 mt-3">
                                    <label for="total_payable" class="form-label"><b>Total Payable Amount</b></label>
                                    <div class="input-group">
                                        <span class="input-group-text">{{ taxInvoiceForm.currency }}</span>
                                        <input type="number" step="0.01" class="form-control"
                                            v-model="taxInvoiceForm.total_payable"
                                            placeholder="Enter total payable amount" required />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="modal-footer">
                            <button type="button" class="btn btn-label-secondary" @click="resetTaxInvoiceForm">
                                Close
                            </button>
                            <button type="submit" class="btn btn-primary" :disabled="taxInvoiceForm.processing">
                                Create Tax Invoice
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </AppLayout>
</template>